import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DeliveryRoute, RoutesQueryParams, RouteResponse, ERPOrdersQueryParams, DeliveryAttempt, OrderItem } from "@/shared/interfaces";
import { Routes } from "@/shared/interfaces/route";

export interface DeliveryState {
  loading: boolean;
  orderLoading: boolean;
  needActionLoading: boolean;
  routes: DeliveryRoute[];
  route: DeliveryRoute;
  currentRouteId: number | null;
  deliveryAttempts: DeliveryAttempt[];
  deliveryOrderDetail: DeliveryAttempt;
  orders: any[];
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
  warehousesSelected: number[];
  currentRoutesOnly: boolean;
  // IDs of routes currently processing (recalculate, disconnect, etc.)
  processingRouteIds: number[];
  unallocatedPickups: OrderItem[];
  unallocatedDeliveries: OrderItem[];
  unallocatedPickupsLoading: boolean;
  unallocatedDeliveriesLoading: boolean;
}

export type DropConnectRoutePayload = { dataPayload: { ids: number[]; routeId: number }; params: RoutesQueryParams };

const initialState: DeliveryState = {
  loading: false,
  orderLoading: false,
  needActionLoading: false,
  routes: [],
  route: {} as DeliveryRoute,
  currentRouteId: null,
  deliveryAttempts: [],
  deliveryOrderDetail: {} as DeliveryAttempt,
  orders: [],
  currentPage: 1,
  itemsPerPage: 3000,
  totalPages: 1,
  warehousesSelected: [],
  currentRoutesOnly: true,
  processingRouteIds: [],
  unallocatedPickups: [],
  unallocatedDeliveries: [],
  unallocatedPickupsLoading: false,
  unallocatedDeliveriesLoading: false,
};

const clearCurrentRouteIfMissing = (state: DeliveryState) => {
  if (state.currentRouteId != null && !state.routes.some((route) => route.id === state.currentRouteId)) {
    state.currentRouteId = null;
    state.route = {} as DeliveryRoute;
  }
};

const deliverySlice = createSlice({
  name: "delivery",
  initialState,
  reducers: {
    getRoutes: (state, action: PayloadAction<RoutesQueryParams>) => {
      // Setting to true will only cause a blinking effect
      state.loading = false;
      state.warehousesSelected = action.payload.warehouseIds ?? [];
      state.currentRoutesOnly = action.payload?.currentRoutesOnly ?? state.currentRoutesOnly;
    },
    getRoutesSuccess: (state, action: PayloadAction<RouteResponse>) => {
      state.loading = false;
      state.routes = action.payload.items;
      state.currentPage = action.payload.meta.currentPage;
      state.itemsPerPage = action.payload.meta.itemsPerPage;
      state.totalPages = action.payload.meta.totalPages;
      clearCurrentRouteIfMissing(state);
    },
    setRoutes: (state, action: PayloadAction<Routes>) => {
      state.routes = action.payload.routes;
      clearCurrentRouteIfMissing(state);
    },
    getRoute: (state, action: PayloadAction<number>) => {
      // Setting to true will only cause a blinking effect
      state.currentRouteId = action.payload;
      state.orderLoading = false;
    },
    getRouteSuccess: (state, action: PayloadAction<DeliveryRoute>) => {
      state.orderLoading = false;
      state.route = action.payload;
      state.currentRouteId = action.payload?.id ?? state.currentRouteId;
    },
    refreshRoute: (state, action: PayloadAction<number>) => {
      // Setting to true will only cause a blinking effect
      state.currentRouteId = action.payload;
      state.orderLoading = false;
    },
    refreshRouteSuccess: (state, action: PayloadAction<DeliveryRoute>) => {
      state.orderLoading = false;
      if (state.currentRouteId === action.payload.id) {
        state.route = action.payload;
      }
    },
    getDeliveryAttempts: (state) => {
      state.needActionLoading = true;
    },
    getDeliveryAttemptsSuccess: (state, action: PayloadAction<DeliveryAttempt[]>) => {
      state.needActionLoading = false;
      state.deliveryAttempts = action.payload;
    },
    getDeliveryAttempt: (state, action: PayloadAction<number>) => {
      state.needActionLoading = true;
    },
    getDeliveryAttemptSuccess: (state, action: PayloadAction<DeliveryAttempt>) => {
      state.needActionLoading = false;
      state.deliveryOrderDetail = action.payload;
    },
    getOrders: (state, action: PayloadAction<ERPOrdersQueryParams>) => {
      state.orderLoading = true;
    },
    getOrdersSuccess: (state, action: PayloadAction<any>) => {
      state.orderLoading = false;
      state.orders = action.payload;
    },
    getOrdersToAppend: (state, action: PayloadAction<ERPOrdersQueryParams>) => {
      state.orderLoading = false;
    },
    getOrdersToAppendSuccess: (state, action: PayloadAction<any>) => {
      if (state.orders.length) {
        state.orders = [...state.orders, ...action.payload];
      }
    },
    openRoute: (state, action: PayloadAction<any>) => {
      state.loading = true;
    },
    openRouteSuccess: (state, action: PayloadAction<RouteResponse>) => {
      state.loading = false;
      state.routes = action.payload.items;
      clearCurrentRouteIfMissing(state);
    },
    disconnectRoute: (state, action: PayloadAction<{ deliveryId: number; routeId: number }>) => {
      state.loading = true;
    },
    disconnectRouteSuccess: (state, action: PayloadAction<DeliveryRoute>) => {
      state.loading = false;
      if (state.currentRouteId === action.payload.id) {
        state.route = action.payload;
      }
    },
    deleteRoute: (state, action: PayloadAction<{ id: number; params: RoutesQueryParams }>) => {
      state.loading = true;
    },
    deleteRouteSuccess: (state, action: PayloadAction<RouteResponse>) => {
      state.loading = false;
      state.routes = action.payload.items;
      state.currentPage = action.payload.meta.currentPage;
      state.itemsPerPage = action.payload.meta.itemsPerPage;
      state.totalPages = action.payload.meta.totalPages;
      clearCurrentRouteIfMissing(state);
    },
    requestFailed: (state) => {
      state.loading = false;
      state.orderLoading = false;
      state.unallocatedPickupsLoading = false;
      state.unallocatedDeliveriesLoading = false;
    },
    recalculateRoute: (state, action: PayloadAction<{ routeId: number }>) => {
      // Setting to true will only cause a blinking effect
      state.orderLoading = false;
    },
    recalculateRouteSuccess: (state, action: PayloadAction<DeliveryRoute>) => {
      state.orderLoading = false;
      if (state.currentRouteId === action.payload.id) {
        state.route = action.payload;
      }
    },
    markProcessingRoute: (state, action: PayloadAction<number>) => {
      const id = action.payload;
      if (!state.processingRouteIds.includes(id)) state.processingRouteIds.push(id);
    },
    unmarkProcessingRoute: (state, action: PayloadAction<number>) => {
      const id = action.payload;
      state.processingRouteIds = state.processingRouteIds.filter((x) => x !== id);
    },
    clearProcessingRoutes: (state) => {
      state.processingRouteIds = [];
    },
    getUnallocatedPickups: (state, _action: PayloadAction<{ limit: number; offset: number; from?: string; to?: string; search?: string; warehouseIds?: number[] }>) => {
      state.unallocatedPickupsLoading = true;
      state.unallocatedPickups = [];
    },
    getUnallocatedPickupsSuccess: (state, action: PayloadAction<OrderItem[]>) => {
      state.unallocatedPickupsLoading = false;
      state.unallocatedPickups = action.payload;
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    getUnallocatedPickupsToAppend: (_state, _action: PayloadAction<{ limit: number; offset: number; from?: string; to?: string; search?: string; warehouseIds?: number[] }>) => {},
    getUnallocatedPickupsToAppendSuccess: (state, action: PayloadAction<OrderItem[]>) => {
      state.unallocatedPickups = [...state.unallocatedPickups, ...action.payload];
    },
    getUnallocatedDeliveries: (state, _action: PayloadAction<{ limit: number; offset: number; from?: string; to?: string; search?: string; warehouseIds?: number[] }>) => {
      state.unallocatedDeliveriesLoading = true;
      state.unallocatedDeliveries = [];
    },
    getUnallocatedDeliveriesSuccess: (state, action: PayloadAction<OrderItem[]>) => {
      state.unallocatedDeliveriesLoading = false;
      state.unallocatedDeliveries = action.payload;
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    getUnallocatedDeliveriesToAppend: (_state, _action: PayloadAction<{ limit: number; offset: number; from?: string; to?: string; search?: string; warehouseIds?: number[] }>) => {},
    getUnallocatedDeliveriesToAppendSuccess: (state, action: PayloadAction<OrderItem[]>) => {
      state.unallocatedDeliveries = [...state.unallocatedDeliveries, ...action.payload];
    },
    dismissUnallocatedOrder: (state, action: PayloadAction<{ orderId: number; listType: "pickups" | "deliveries" }>) => {
      const { orderId, listType } = action.payload;
      if (listType === "pickups") {
        state.unallocatedPickups = state.unallocatedPickups.filter((o) => o.id !== orderId);
      } else {
        state.unallocatedDeliveries = state.unallocatedDeliveries.filter((o) => o.id !== orderId);
      }
    },
    removeUnallocatedOrders: (state, action: PayloadAction<{ orderIds: number[]; listType: "pickups" | "deliveries" }>) => {
      const { orderIds, listType } = action.payload;
      const idsSet = new Set(orderIds);
      if (listType === "pickups") {
        state.unallocatedPickups = state.unallocatedPickups.filter((o) => !idsSet.has(o.id));
      } else {
        state.unallocatedDeliveries = state.unallocatedDeliveries.filter((o) => !idsSet.has(o.id));
      }
    },
  },
});

export const deliveryActions = deliverySlice.actions;

export default deliverySlice.reducer;
